import {
  AtSymbolIcon as AtSymbolIconOutline,
  BeakerIcon as BeakerIconOutline,
  CircleStackIcon as CircleStackIconOutline
} from '@heroicons/react/24/outline'
import { CloudArrowDownIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import { QRCodeCanvas } from 'qrcode.react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../../../constants/track-events'
import { useNotificationContext } from '../../../context'
import { BUILD_ENV } from '../../../helpers'
import { trackEvent } from '../../../helpers/analytics'
import { useSetState } from '../../../hooks/use-set-state'
import InviteLink from '../../invite-link'
import Button from '../../tailwind/Button'
import Modal from '../../tailwind/Modal'
import Actions from '../../tailwind/actions'
import Steps from '../../tailwind/steps'
import AdditionalFields from './additional-fields'
import Challenges from './challenges'
import Description from './description'
import GeneralFields from './general-fields'

// const STATES = {
const ACTIONS = 'ACTIONS'
const GENERAL_FIELDS = 'GENERAL_FIELDS'
const ADDITIONAL_FIELDS = 'ADDITIONAL_FIELDS'
const CHALLENGES = 'CHALLENGES'
const DESCRIPTION = 'DESCRIPTION'
// }

const getPbcLink = (type, id) => {
  const DEVELOP_URL = new URL('https://develop.pbc.aivy.app/invitation/')
  const STAGING_URL = new URL('https://staging.pbc.aivy.app/invitation')
  const PRODUCTION_URL = new URL('https://pbc.aivy.app/invitation/')

  const param = {
    name: type === 'career' ? 'career' : 'partner',
    value: id
  }

  DEVELOP_URL.searchParams.set(param.name, param.value)
  STAGING_URL.searchParams.set(param.name, param.value)
  PRODUCTION_URL.searchParams.set(param.name, param.value)

  switch (BUILD_ENV) {
    case 'develop':
      return DEVELOP_URL.toString()
    case 'staging':
      return STAGING_URL.toString()
    default:
      return PRODUCTION_URL.toString()
  }
}

const FlexConfiguration = ({
  open,
  setOpen,
  entity,
  updateHandler,
  create,
  update,
  type // 'career' or 'partner'
}) => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()

  const [state, setState] = useSetState({
    current: (create && GENERAL_FIELDS) || (update && ACTIONS),
    isLoading: false,
    isEditMode: false
  })

  const { current, isLoading, isEditMode } = state

  const submitData = ({ data, onSuccess, onError }) => {
    const {
      customLandingPageText,
      customLandingPageFields,
      application_input_additional_fields: aiaf,
      challenges
    } = data

    const app_settings = entity.app_settings || {}
    const components = JSON.parse(app_settings.components || '{}')

    app_settings.application_input_additional_fields = JSON.stringify(aiaf)
    components.customLandingPageFields = customLandingPageFields
    components.customLandingPageText = customLandingPageText

    // Handle challenges for partner type
    if (type === 'partner' && challenges !== undefined) {
      app_settings.challenges = challenges
    }

    app_settings.components = JSON.stringify(components)

    updateHandler({
      input: { id: entity.id, app_settings },
      onSuccess,
      onError
    })
  }

  // Create steps array based on type
  const baseSteps = [
    {
      id: '01',
      name: t('career.flex.general_fields.title'),
      description: t('career.flex.general_fields.description'),
      current: current === GENERAL_FIELDS,
      complete: false,
      action: {
        onClick: () => setState({ current: GENERAL_FIELDS }),
        iconColor: 'bg-blue-400/90 group-hover:bg-blue-400',
        icon: AtSymbolIconOutline
      }
    },
    {
      id: '02',
      name: t('career.flex.additional_fields.title'),
      description: t('career.flex.additional_fields.description'),
      current: current === ADDITIONAL_FIELDS,
      complete: false,
      action: {
        onClick: () => setState({ current: ADDITIONAL_FIELDS }),
        iconColor: 'bg-indigo-400/90 group-hover:bg-indigo-400',
        icon: CircleStackIconOutline
      }
    }
  ]

  // Add challenges step only for partner type
  if (type === 'partner') {
    baseSteps.push({
      id: '03',
      name: t('career.flex.challenges.title'),
      description: t('career.flex.challenges.description'),
      current: current === CHALLENGES,
      complete: false,
      action: {
        onClick: () => setState({ current: CHALLENGES }),
        iconColor: 'bg-purple-400/90 group-hover:bg-purple-400',
        icon: BeakerIconOutline
      }
    })
  }

  // Add description step
  baseSteps.push({
    id: type === 'partner' ? '04' : '03',
    name: t('career.flex.description.title'),
    description: t('career.flex.description.description'),
    current: current === DESCRIPTION,
    complete: false,
    action: {
      onClick: () => setState({ current: DESCRIPTION }),
      iconColor: 'bg-cyan-400/90 group-hover:bg-cyan-400',
      icon: AtSymbolIconOutline // Using AtSymbolIconOutline as fallback
    }
  })

  const steps = baseSteps

  const handleClose = () => {
    setOpen(false)
    setTimeout(
      () =>
        setState({
          current: (create && GENERAL_FIELDS) || (update && ACTIONS),
          isEditMode: false
        }),
      1000 * 0.6
    )
  }

  const getCurrentValues = () => {
    const { app_settings } = entity
    const { customLandingPageFields, customLandingPageText } = JSON.parse(
      app_settings?.components || '{}'
    )

    return {
      customLandingPageText,
      customLandingPageFields,
      application_input_additional_fields: JSON.parse(
        app_settings?.application_input_additional_fields || '[]'
      )
    }
  }

  const updateWithState = (s) => {
    setState({ ...s, isLoading: true })

    submitData({
      data: { ...getCurrentValues(), ...s },
      onSuccess: () => {
        // Nach dem Speichern zurück zum Auswahlbildschirm navigieren
        setState({ current: ACTIONS, isLoading: false, isEditMode: true })
        success(t('career.flex.create_flex.update_success'))
        trackEvent(trackEvents.UPDATE_PUBLIC_LINK_INVITATION)
      },
      onError: () => setState({ isLoading: false })
    })
  }

  const handleUpdateGeneralFields = (s) => {
    if (create) setState({ ...s, current: ADDITIONAL_FIELDS })
    else if (update) updateWithState(s)
  }

  const handleUpdateAdditionalFields = (s) => {
    if (create) {
      // For partner type, go to challenges, otherwise go to description
      const nextStep = type === 'partner' ? CHALLENGES : DESCRIPTION
      setState({ ...s, current: nextStep })
    } else if (update) updateWithState(s)
  }

  const handleUpdateChallenges = (s) => {
    if (create) setState({ ...s, current: DESCRIPTION })
    else if (update) updateWithState(s)
  }

  const handleUpdateDescription = (s) => {
    if (create) {
      setState({ ...s, isLoading: true })
      // pass data because set state is async
      submitData({
        data: { ...state, ...s },
        onSuccess: () => {
          setState({ current: GENERAL_FIELDS, isLoading: false })
          handleClose()
          success(t('career.flex.create_flex.create_success'))
          trackEvent(trackEvents.ACTIVATE_PUBLIC_LINK_INVITATION)
        },
        onError: () => setState({ isLoading: false })
      })
    } else if (update) updateWithState(s)
  }

  const backHandler = () => {
    setState({ current: ACTIONS, isEditMode: false })
  }

  const components = JSON.parse(entity.app_settings?.components || '{}')
  // If customLandingPageFields doesn't exist, we're in create mode
  // If isEditMode is true, we're in edit mode
  // If current is not ACTIONS, we're in edit mode
  const editMode =
    !components.customLandingPageFields || isEditMode || current !== ACTIONS

  const downloadQRCodeCanvas = () => {
    const a = document.createElement('a')
    const qrcode = document.getElementById('qr-code-flex')

    a.download = `aivy-${type === 'career' ? 'career' : 'talentpool'}.png`
    a.href = qrcode.toDataURL()
    a.click()
  }

  return (
    <Modal open={open} setOpen={handleClose} size='4xl'>
      {create && <Steps steps={steps} />}
      {!editMode ? (
        <div className='pt-8'>
          {/* ACTIVE MODE - Show link and QR code */}
          <div className='rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
            <h2 className='mb-4 text-lg font-semibold text-gray-900'>
              {t(
                type === 'career'
                  ? 'career.flex.url_title'
                  : 'career.flex.url_title_partner'
              )}
            </h2>
            <div className='max-w-3xl'>
              {type === 'career' ? (
                <InviteLink careerID={entity.id} />
              ) : (
                <InviteLink partnerID={entity.id} />
              )}
            </div>
          </div>

          <div className='mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
            <h2 className='mb-2 text-lg font-semibold text-gray-900'>
              {t('career.flex.feature_section.qrcode_name')}
            </h2>
            <p className='mb-4 max-w-lg text-sm text-gray-700 lg:max-w-2xl'>
              {t('career.flex.feature_section.qrcode_description')}
            </p>
            <div className='flex flex-col items-center'>
              <QRCodeCanvas
                id='qr-code-flex'
                value={getPbcLink(type, entity.id)}
                size={160}
                bgColor={'#ffffff'}
                fgColor={'#000000'}
                level={'L'}
                includeMargin={false}
              />
              <div
                style={{ maxWidth: 160 }}
                onClick={downloadQRCodeCanvas}
                className='group mt-3 flex cursor-pointer items-center justify-center rounded-md bg-gray-50 px-3 py-2 hover:bg-blue-600'
              >
                <CloudArrowDownIcon className='h-5 w-5 text-gray-500 group-hover:text-white' />
                <span className='ml-1 text-xs font-semibold text-gray-700 group-hover:text-white'>
                  {t('career.flex.qr_code_download_action')}
                </span>
              </div>
            </div>
          </div>

          <div className='mt-8 flex justify-end'>
            <Button.SecondaryLG
              text={t('career.flex.update_action')}
              onClick={() => {
                // Set isEditMode to true to show the edit mode
                setState({ current: ACTIONS, isEditMode: true })
              }}
            />
          </div>
        </div>
      ) : (
        <div className='pt-6'>
          <div className='mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
            <div className='flex flex-col justify-center'>
              <h2 className='mb-1 text-xl font-semibold text-gray-900'>
                {steps.find(({ current: c }) => c)?.name}
              </h2>
              <span className='text-sm text-gray-700'>
                {steps.find(({ current: c }) => c)?.description}
              </span>
            </div>

            <div className='mt-6'>
              {current === GENERAL_FIELDS && (
                <GeneralFields
                  entity={entity}
                  updateHandler={handleUpdateGeneralFields}
                  backHandler={backHandler}
                  create={create}
                  update={update}
                  isLoading={isLoading}
                  type={type}
                />
              )}
              {current === ADDITIONAL_FIELDS && (
                <AdditionalFields
                  entity={entity}
                  updateHandler={handleUpdateAdditionalFields}
                  backHandler={backHandler}
                  create={create}
                  update={update}
                  isLoading={isLoading}
                  type={type}
                />
              )}
              {current === CHALLENGES && type === 'partner' && (
                <Challenges
                  entity={entity}
                  updateHandler={handleUpdateChallenges}
                  backHandler={backHandler}
                  create={create}
                  update={update}
                  isLoading={isLoading}
                  type={type}
                />
              )}
              {current === DESCRIPTION && (
                <Description
                  entity={entity}
                  updateHandler={handleUpdateDescription}
                  backHandler={backHandler}
                  create={create}
                  update={update}
                  isLoading={isLoading}
                  type={type}
                />
              )}
              {current === ACTIONS && (
                <div className='pb-4'>
                  <Actions
                    title={t('career.flex.title')}
                    description={t('career.flex.description', {
                      career_title:
                        entity.title ||
                        (type === 'partner'
                          ? t('talents.talentpool_title')
                          : '')
                    })}
                    actions={steps.map((step) => ({ ...step, ...step.action }))}
                  />
                  <div className='mt-8 flex justify-end'>
                    <Button.SecondaryLG
                      text={t('career.flex.back_to_overview')}
                      onClick={() => setState({ isEditMode: false })}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

FlexConfiguration.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  entity: PropTypes.object.isRequired,
  updateHandler: PropTypes.func.isRequired,
  create: PropTypes.bool,
  update: PropTypes.bool,
  type: PropTypes.oneOf(['career', 'partner']).isRequired
}

FlexConfiguration.defaultProps = {
  create: false,
  update: false
}

export default FlexConfiguration
